import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Car, FileDown, Save, CheckCircle2, AlertTriangle, XCircle, MinusCircle, Camera, Trash2, Sparkles } from "lucide-react";

const STATUSES = [
  { id: "ok", label: "OK", cls: "text-emerald-500 border-emerald-500/40 bg-emerald-500/10", icon: CheckCircle2 },
  { id: "atencao", label: "Atenção", cls: "text-orange-500 border-orange-500/40 bg-orange-500/10", icon: AlertTriangle },
  { id: "urgente", label: "Urgente", cls: "text-red-500 border-red-500/40 bg-red-500/10", icon: XCircle },
  { id: "na", label: "N/A", cls: "text-muted-foreground border-border bg-muted", icon: MinusCircle },
];

export default function InspecaoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [obs, setObs] = useState("");
  const [priority, setPriority] = useState("monitorar");
  const [photos, setPhotos] = useState([]);
  const [photosByKey, setPhotosByKey] = useState({});
  const [requiredPhotos, setRequiredPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [ccc, setCcc] = useState(null);
  const [cccLoading, setCccLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/inspections/${id}`);
    setData(data);
    setItems(data.inspection.items || []);
    setObs(data.inspection.observations || "");
    setPriority(data.inspection.overall_priority || "monitorar");
    setPhotos(data.inspection.photos || []);
    setPhotosByKey(data.inspection.photos_by_key || {});
    setRequiredPhotos(data.required_photos || []);
    setCcc(data.inspection.ccc || null);
  };
  useEffect(() => { load(); }, [id]);

  const setItemStatus = (itemId, status) => setItems((prev) => prev.map((it) => it.id === itemId ? { ...it, status } : it));
  const setItemNotes = (itemId, notes) => setItems((prev) => prev.map((it) => it.id === itemId ? { ...it, notes } : it));

  const grouped = useMemo(() => {
    const g = {};
    for (const it of items) (g[it.category] = g[it.category] || []).push(it);
    return g;
  }, [items]);

  const counts = useMemo(() => ({
    ok: items.filter((x) => x.status === "ok").length,
    atencao: items.filter((x) => x.status === "atencao").length,
    urgente: items.filter((x) => x.status === "urgente").length,
    na: items.filter((x) => x.status === "na").length,
  }), [items]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/inspections/${id}`, { items, observations: obs, overall_priority: priority, photos, photos_by_key: photosByKey });
      toast.success("Inspeção salva");
      await load();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const finalize = async () => {
    // Verifica fotos obrigatórias
    const missing = requiredPhotos.filter((rp) => !photosByKey[rp.key]);
    if (missing.length > 0) {
      toast.error(`Fotos obrigatórias faltando: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    if (!window.confirm("Finalizar esta inspeção? Após finalização o cliente pode visualizar o relatório completo.")) return;
    setFinalizing(true);
    try {
      await api.put(`/inspections/${id}`, {
        items, observations: obs, overall_priority: priority, photos, photos_by_key: photosByKey, finalized: true,
      });
      toast.success("Inspeção finalizada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao finalizar");
    } finally { setFinalizing(false); }
  };

  const uploadRequiredPhoto = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const token = localStorage.getItem("aab_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}${token ? `?auth=${token}` : ""}`;
      setPhotosByKey((prev) => ({ ...prev, [key]: url }));
      toast.success("Foto adicionada");
    } catch { toast.error("Erro no upload"); }
    finally { setUploadingKey(null); e.target.value = ""; }
  };

  const removeRequiredPhoto = (key) => {
    setPhotosByKey((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const token = localStorage.getItem("aab_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}${token ? `?auth=${token}` : ""}`;
      setPhotos((p) => [...p, url]);
      toast.success("Foto adicionada");
    } catch { toast.error("Erro no upload"); }
    finally { setUploading(false); }
    e.target.value = "";
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const generateCCC = async () => {
    setCccLoading(true);
    try {
      const { data } = await api.post(`/inspections/${id}/ccc`, {});
      setCcc(data);
      toast.success("Complaint / Cause / Correction gerados");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar");
    } finally { setCccLoading(false); }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const openPdf = async () => {
    if (downloadingPdf) return;
    setDownloadingPdf(true);
    // Feedback imediato — evita "tela branca" caso o servidor demore
    const loadingToast = toast.loading("Gerando PDF da inspeção…");
    try {
      const res = await api.get(`/inspections/${id}/pdf`, {
        responseType: "blob",
        // Aceita qualquer status para poder ler o body de erro se falhar
        validateStatus: () => true,
      });
      if (res.status !== 200) {
        // Tenta extrair detalhe do erro do body (que pode ser texto ou JSON)
        let detail = `Erro ${res.status} ao gerar PDF`;
        try {
          const text = await res.data.text();
          try {
            const j = JSON.parse(text);
            if (j?.detail) detail = j.detail;
          } catch { if (text && text.length < 200) detail = text; }
        } catch { /* keep default */ }
        toast.error(detail, { id: loadingToast });
        return;
      }
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Abre em nova aba; se popup bloqueado, força download
      const w = window.open(url, "_blank");
      if (!w) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `inspecao-${data?.vehicle?.plate || data?.vehicle?.vin || id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Libera memória após um tempo (aba já leu o blob)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("PDF pronto", { id: loadingToast });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Falha ao gerar PDF. Tente novamente.", { id: loadingToast });
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (!data) return <div className="text-sm text-muted-foreground">Carregando…</div>;
  const { vehicle, client, job } = data;

  return (
    <div className="space-y-6" data-testid="inspecao-detalhe-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2"><ArrowLeft className="w-4 h-4" /> Voltar</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openPdf} disabled={downloadingPdf} data-testid="pdf-download-btn">
            <FileDown className="w-4 h-4" /> {downloadingPdf ? "Gerando…" : "PDF"}
          </Button>
          <Button variant="outline" onClick={save} disabled={saving} data-testid="inspection-save-btn"><Save className="w-4 h-4" /> {saving ? "Salvando…" : "Salvar"}</Button>
          <Button
            onClick={finalize}
            disabled={finalizing || data?.inspection?.finalized}
            data-testid="inspection-finalize-btn"
          >
            <CheckCircle2 className="w-4 h-4" /> {data?.inspection?.finalized ? "Finalizada" : (finalizing ? "Finalizando…" : "Finalizar")}
          </Button>
        </div>
      </div>

      {/* Header */}
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <div className="h-40 bg-secondary">
          {vehicle?.photo_url ? <img src={vehicle.photo_url} alt="" className="w-full h-full object-cover" /> :
            <div className="w-full h-full flex items-center justify-center"><Car className="w-10 h-10 text-muted-foreground" /></div>}
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Veículo</div>
            <div className="font-display text-lg font-bold">{vehicle?.year} {vehicle?.make} {vehicle?.model}</div>
            <div className="text-xs font-mono text-muted-foreground">
              License Plate: <b>{vehicle?.plate || "—"}</b> · VIN {vehicle?.vin?.slice(-6) || "—"}
              {vehicle?.mileage ? <> · <b>{vehicle.mileage.toLocaleString("en-US")} mi</b></> : null}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cliente</div>
            <div className="font-display text-lg font-bold">{client?.name}</div>
            <div className="text-xs text-muted-foreground">{client?.phone}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Serviço</div>
            <div className="font-display text-lg font-bold">{job?.service_type}</div>
            <div className="text-xs text-muted-foreground">Técnico: {job?.technician || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Prioridade Geral</div>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="mt-1" data-testid="priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="urgente">Urgente</SelectItem>
                <SelectItem value="importante">Importante</SelectItem>
                <SelectItem value="monitorar">Monitorar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-4 gap-3" data-testid="inspection-counts">
        {STATUSES.map((s) => (
          <div key={s.id} className={`border rounded-md p-3 ${s.cls}`}>
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest">{s.label}</div>
              <s.icon className="w-4 h-4" />
            </div>
            <div className="font-display text-2xl font-bold mt-1">{counts[s.id]}</div>
          </div>
        ))}
      </div>

      {/* Checklist */}
      <div className="space-y-6">
        {Object.entries(grouped).map(([category, itms]) => (
          <div key={category} className="border border-border rounded-md bg-card overflow-hidden">
            <div className="px-4 py-2.5 bg-secondary/60 border-b border-border">
              <div className="text-sm font-semibold uppercase tracking-wider">{category}</div>
            </div>
            <div className="divide-y divide-border">
              {itms.map((it) => (
                <div key={it.id} className="px-4 py-3 grid grid-cols-12 gap-3 items-center" data-testid={`insp-item-${it.id}`}>
                  <div className="col-span-4 text-sm">{it.name}</div>
                  <div className="col-span-4 flex gap-1.5 flex-wrap">
                    {STATUSES.map((s) => {
                      const active = it.status === s.id;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setItemStatus(it.id, s.id)}
                          className={`px-2 py-1 rounded border text-[11px] font-medium transition-colors ${active ? s.cls : "border-border text-muted-foreground hover:bg-accent"}`}
                          data-testid={`item-${it.id}-status-${s.id}`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="col-span-4">
                    <Input
                      value={it.notes || ""}
                      onChange={(e) => setItemNotes(it.id, e.target.value)}
                      placeholder="Observações do item…"
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Required Photos (mandatory) */}
      <div className="border border-border rounded-md bg-card p-5" data-testid="required-photos-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Fotos Obrigatórias</div>
            <div className="font-display font-bold">Required Photo Documentation</div>
            <div className="text-xs text-muted-foreground mt-1">
              Todas as fotos abaixo devem ser adicionadas para finalizar a inspeção.
            </div>
          </div>
          <div className="text-[11px] font-mono">
            {requiredPhotos.filter((rp) => photosByKey[rp.key]).length} / {requiredPhotos.length}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {requiredPhotos.map((rp) => {
            const url = photosByKey[rp.key];
            const isUploading = uploadingKey === rp.key;
            return (
              <div key={rp.key} className={`border rounded-md overflow-hidden ${url ? "border-emerald-500/40" : "border-dashed border-border"}`} data-testid={`req-photo-${rp.key}`}>
                <label className={`block relative h-28 cursor-pointer ${url ? "" : "bg-secondary/40 hover:bg-secondary"}`}>
                  {url ? (
                    <>
                      <img src={url} alt={rp.label} className="w-full h-full object-cover" />
                      <div className="absolute top-1 right-1 flex gap-1">
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); removeRequiredPhoto(rp.key); }}
                          className="p-1 rounded bg-black/70 text-white hover:bg-red-600"
                          title="Remover"
                          data-testid={`remove-req-${rp.key}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-emerald-500 text-white rounded text-[9px] font-bold flex items-center gap-0.5">
                        <CheckCircle2 className="w-2.5 h-2.5" /> OK
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground gap-1">
                      {isUploading ? (
                        <div className="text-[10px]">Enviando…</div>
                      ) : (
                        <>
                          <Camera className="w-6 h-6" />
                          <div className="text-[10px] font-medium">Adicionar</div>
                        </>
                      )}
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadRequiredPhoto(e, rp.key)}
                    data-testid={`input-req-${rp.key}`}
                  />
                </label>
                <div className="p-2 text-[11px] font-medium leading-tight text-center border-t border-border">
                  {rp.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Additional Photos (não obrigatórias) */}
      <div className="border border-border rounded-md bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Fotos Extras</div>
            <div className="font-display font-bold">Evidências visuais adicionais</div>
            <div className="text-xs text-muted-foreground mt-0.5">Opcional. Use para documentar itens específicos.</div>
          </div>
          <label className="inline-flex items-center gap-2 px-3 h-9 rounded-md border border-input hover:bg-accent cursor-pointer text-sm">
            <Camera className="w-4 h-4" />
            {uploading ? "Enviando…" : "Adicionar foto"}
            <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="insp-photo-input" />
          </label>
        </div>
        {photos.length === 0 ? <div className="text-sm text-muted-foreground">Nenhuma foto anexada.</div> :
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {photos.map((p, idx) => (
              <div key={idx} className="relative group">
                <img src={p} alt="" className="w-full h-24 object-cover rounded border border-border" />
                <button
                  onClick={() => removePhoto(idx)}
                  className="absolute top-1 right-1 p-1 rounded bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-photo-${idx}`}
                ><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>}
      </div>

      {/* Observações */}
      <div className="border border-border rounded-md bg-card p-5">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Observações do Técnico</div>
        <div className="font-display font-bold mb-3">Recomendações gerais</div>
        <Textarea rows={5} value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Ex: Veículo em bom estado geral. Recomendo revisar suspensão em 5000 km." data-testid="observations-textarea" />
      </div>

      {/* Complaint / Cause / Correction — gerado por IA */}
      <div className="border border-border rounded-md bg-card p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
              Complaint · Cause · Correction
              <Sparkles className="w-3 h-3 text-primary" />
            </div>
            <div className="font-display font-bold">Laudo técnico gerado por IA</div>
            <p className="text-xs text-muted-foreground mt-0.5">Usado em relatórios de garantia estendida (formato americano).</p>
          </div>
          <Button variant={ccc ? "outline" : "default"} onClick={generateCCC} disabled={cccLoading} data-testid="ccc-generate-btn">
            <Sparkles className="w-4 h-4" />
            {cccLoading ? "Gerando…" : ccc ? "Regenerar" : "Gerar com IA"}
          </Button>
        </div>
        {ccc && (
          <div className="mt-4 space-y-3" data-testid="ccc-result">
            {[
              ["Complaint", ccc.complaint],
              ["Cause", ccc.cause],
              ["Correction", ccc.correction],
            ].map(([label, value]) => (
              <div key={label} className="border-l-2 border-primary pl-3">
                <div className="text-[10px] uppercase tracking-widest text-primary mb-1">{label}</div>
                <div className="text-sm whitespace-pre-line leading-relaxed">{value || "—"}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
